import { LayoutModule } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InputSignal,
  ModelSignal,
  Signal,
  computed,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatDrawerMode, MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { Observable, merge } from 'rxjs';
import { map } from 'rxjs/operators';

import { CoreInsightsEvent, InsightsContextAware, RouterEventsService, ViewportService } from '@core/shared/util';

import { AppHeaderComponent } from '../app-header';

@Component({
  selector: 'mp-shell',
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mp-shell',
  },
  imports: [
    RouterModule,
    LayoutModule,

    MatToolbar,
    MatProgressBar,
    MatSidenavContainer,
    MatSidenav,
    MatSidenavContent,
    MatIcon,
    MatButton,
    MatDivider,

    AppHeaderComponent,
  ],
})
export class ShellComponent extends InsightsContextAware {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly routerEventsService: RouterEventsService = inject(RouterEventsService);

  readonly appTitle: InputSignal<string> = input.required<string>();

  readonly isOpened: ModelSignal<boolean> = model.required<boolean>();

  readonly showAppLogo: InputSignal<boolean> = input.required<boolean>();

  readonly sideNav: Signal<MatSidenav> = viewChild.required('sidenav');

  readonly sideNavRef: Signal<ElementRef<HTMLElement>> = viewChild.required('sidenav', {
    read: ElementRef<HTMLElement>,
  });

  readonly isLoading: Signal<boolean> = toSignal(this.buildIsLoadingObservable(), { initialValue: false });

  protected readonly isMediumViewport: Signal<boolean> = toSignal(this.viewportService.isMediumViewport$, {
    requireSync: true,
  });

  protected readonly sidenavMode: Signal<MatDrawerMode> = computed(() => (this.isMediumViewport() ? 'over' : 'side'));

  toggleSidenav(): void {
    const event: CoreInsightsEvent = this.isOpened()
      ? CoreInsightsEvent.NAVIGATION_MENU_COLLAPSE
      : CoreInsightsEvent.NAVIGATION_MENU_EXPAND;

    this.trackInsightsEvent(event, {}, { includePageName: false });

    this.sideNav().toggle();
  }

  private buildIsLoadingObservable(): Observable<boolean> {
    return merge(
      this.routerEventsService.navigationStart$.pipe(map(() => true)),
      this.routerEventsService.navigationEndCancelError$.pipe(map(() => false)),
    );
  }
}
